/**
 * How one intake slot sends its files.
 *
 * A slot posts a file the moment it is chosen, unless the slot has tags.
 * Supporting material has them: nothing can classify its contents, so the
 * attorney says what each file is first, one file at a time, and the file and
 * its tag are posted together. That staging is
 * client-only state the server has never seen, which is why it lives here
 * beside the slot rather than in `UploadComponent`, which stays presentational,
 * or in react-query, which holds what the server knows.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type {
  FileSource,
  FileType,
  FileUploadResponse,
} from "@patent-studio/api-types";
import { refusalDetail } from "@/services/api-client";
import { uploadFile } from "@/services/files";
import type { PendingDescription } from "@/lib/supporting-description";
import type { IntakeComponentSpec, SlotTag } from "@/lib/intake-components";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/** One file on its way to the server, and where a refusal of it belongs. */
interface UploadVars {
  file: File;
  fileType: FileType;
  description?: string;
  supportingTag?: SlotTag["id"];
  /** `row` for a staged file, which shows its own message; `slot` otherwise. */
  reportTo: "slot" | "row";
}

/** A file chosen but not yet sent, with the words written about it so far. */
export interface PendingUpload extends PendingDescription {
  file: File;
}

export interface SlotUploads {
  /** An upload is in flight for this slot. */
  busy: boolean;
  /** Files chosen here and not sent, or sent and refused where the slot keeps those. */
  pending: readonly PendingUpload[];
  select: (files: File[]) => void;
  /** Mark a chosen file with a tag that still needs words written for it. */
  choose: (id: string, tag: SlotTag["id"]) => void;
  write: (id: string, description: string) => void;
  /** Send one chosen file with its tag, and the words written for it where the tag asks. */
  add: (id: string, tag: SlotTag["id"]) => void;
  discard: (id: string) => void;
  /** Put down every file still waiting to be told what it is. */
  discardAll: () => void;
  /** Send one refused row again. */
  retry: (id: string) => void;
}

// A file name is not unique enough: the same document can be chosen twice.
let lastId = 0;
const nextId = () => `pending-${(lastId += 1)}`;

/**
 * The API names what it refused and why, and that sentence is the useful one.
 * A generic message tells an attorney nothing about what to do next.
 */
function refusal(err: unknown, file: File): string {
  return refusalDetail(err, `Could not add ${file.name}.`);
}

/**
 * Tells the page how the staged count moved, after each render that moved it.
 * Reported from an effect, not from inside a state update, where it would
 * update the page while this slot renders.
 */
function useStagedCount(count: number, onStaged: (delta: number) => void) {
  const reportedRef = useRef(0);
  useEffect(() => {
    const delta = count - reportedRef.current;
    if (delta === 0) return;
    reportedRef.current = count;
    onStaged(delta);
  }, [count, onStaged]);
}

/** The staged rows and the things that happen to one. */
function usePendingRows(onStaged: (delta: number) => void) {
  const [rows, setRows] = useState<PendingUpload[]>([]);
  useStagedCount(rows.length, onStaged);

  const update = useCallback(
    (id: string, change: (row: PendingUpload) => PendingUpload) =>
      setRows((current) =>
        current.map((row) => (row.id === id ? change(row) : row)),
      ),
    [],
  );

  return {
    rows,
    update,
    stage: (files: File[]) => {
      setRows((current) => [
        ...current,
        ...files.map((file) => ({
          id: nextId(),
          fileName: file.name,
          contentType: file.type,
          tag: null,
          description: "",
          status: "idle" as const,
          file,
        })),
      ]);
    },
    // A file the server refused, kept with its reason so the attorney can try
    // it again without choosing it a second time.
    keep: (file: File, message: string) => {
      setRows((current) => [
        ...current,
        {
          id: nextId(),
          fileName: file.name,
          contentType: file.type,
          tag: null,
          description: "",
          status: "failed" as const,
          message,
          file,
        },
      ]);
    },
    write: useCallback(
      (id: string, description: string) =>
        update(id, (row) => ({ ...row, description })),
      [update],
    ),
    // Choosing the tag a row already has keeps what was written for it.
    choose: useCallback(
      (id: string, tag: SlotTag["id"]) =>
        update(id, (row) =>
          row.tag === tag ? row : { ...row, tag, description: "" },
        ),
      [update],
    ),
    // A row leaves the panel two ways: discarded, or added by the server. Both
    // reach here, so the count the page holds follows either.
    discard: useCallback(
      (id: string) => setRows((current) => current.filter((r) => r.id !== id)),
      [],
    ),
    // A row already sending is not the attorney's to put down: its answer, a
    // refusal above all, still needs a row to land on.
    discardAll: useCallback(
      () => setRows((current) => current.filter((r) => r.status === "sending")),
      [],
    ),
  };
}

/**
 * How one slot posts a file, and what a refusal does.
 *
 * One mutation per component, not one for the page. A shared one reports
 * every component busy whenever any of them is uploading, which took the
 * controls away mid-task.
 */
function useSlotMutation({
  taskId,
  source,
  onRefused,
  onUploaded,
  onError,
  onSettled,
  onInFlight,
}: {
  taskId: string | undefined;
  source: FileSource;
  onUploaded?: (uploaded: FileUploadResponse, file: File) => void;
  /** Where a slot keeps a refused file as a row: told the file and the reason, instead of the shared line. */
  onRefused?: (file: File, message: string) => void;
  onError: (message: string | null) => void;
  onSettled: () => Promise<void>;
  onInFlight: (delta: number) => void;
}) {
  // Counted here rather than read from `upload.isPending`. One mutation
  // observer per slot reports only the most recently started call, so with
  // several files in flight it goes idle as soon as the last-started one
  // settles, and the control stops saying so while earlier files are still
  // going. Both paths below send more than one file at a time.
  const [inFlight, setInFlight] = useState(0);
  const started = useCallback(() => {
    setInFlight((n) => n + 1);
    onInFlight(1);
  }, [onInFlight]);

  // Refusals from one selection, composed into the slot's single error line.
  // Cleared when the next selection starts rather than when one lands, so
  // three files refused together are all named instead of overwriting
  // each other down to whichever finished last.
  const refusedRef = useRef<string[]>([]);

  const upload = useMutation({
    mutationKey: MUTATION_KEYS.fileUpload,
    mutationFn: ({
      file,
      fileType,
      description,
      supportingTag,
    }: UploadVars) => {
      if (!taskId) return Promise.reject(new Error("No draft selected"));
      // Whether a blank description travels at all is settled in uploadFile,
      // so every caller sends the same shape.
      return uploadFile(
        taskId,
        file,
        fileType,
        description,
        source,
        supportingTag,
      );
    },
    onSuccess: (uploaded, variables) => {
      onUploaded?.(uploaded, variables.file);
      return onSettled();
    },
    // Declared on the mutation, not passed per call. `MutationObserver.mutate`
    // overwrites the per-call options and drops the previous mutation, so a
    // handler passed inside a loop only ever survives for the last file: the
    // rest were refused in silence. Staged rows report on their own row, so
    // only slot-reported failures reach the shared line.
    onError: (err, variables) => {
      if (variables.reportTo !== "slot") return;
      if (onRefused) {
        onRefused(variables.file, refusal(err, variables.file));
        return;
      }
      refusedRef.current = [
        ...refusedRef.current,
        refusal(err, variables.file),
      ];
      onError(refusedRef.current.join(" "));
    },
    onSettled: () => {
      setInFlight((n) => n - 1);
      onInFlight(-1);
    },
  });

  return {
    upload,
    started,
    busy: inFlight > 0,
    clearRefusals: () => {
      refusedRef.current = [];
    },
  };
}

/**
 * Send one staged file and let its own row report the outcome. A file that
 * lands leaves the panel; one that fails keeps its words, so a second click
 * retries it and no paragraph is lost.
 *
 * Awaited per row rather than through `mutate`'s callbacks: one observer keeps
 * only the last call's callbacks, so two files sent together would report a
 * single outcome between them.
 */
function sendRow({
  pending,
  upload,
  started,
}: {
  pending: ReturnType<typeof usePendingRows>;
  upload: ReturnType<typeof useSlotMutation>["upload"];
  started: () => void;
}) {
  return (row: PendingUpload, fileType: FileType, tag?: SlotTag) => {
    started();
    pending.update(row.id, (r) => ({ ...r, status: "sending", message: null }));
    void upload
      .mutateAsync({
        file: row.file,
        fileType,
        supportingTag: tag?.id,
        // Only a tag that asks for words sends any; a kept refusal has none.
        description:
          tag !== undefined && tag.descriptionMin !== null
            ? row.description
            : undefined,
        reportTo: "row",
      })
      .then(() => pending.discard(row.id))
      .catch((err: unknown) =>
        pending.update(row.id, (r) => ({
          ...r,
          status: "failed",
          message: refusal(err, row.file),
        })),
      );
  };
}

export function useSlotUploads({
  spec,
  taskId,
  source,
  keepRefused = false,
  onUploaded,
  onError,
  onSettled,
  onInFlight,
  onStaged,
}: {
  spec: IntakeComponentSpec;
  taskId: string | undefined;
  /**
   * Whether a file the server refused stays on the screen as a row carrying
   * the reason, with a way to send it again. Off, a refusal is one line under
   * the control, which is what the intake slots show.
   */
  keepRefused?: boolean;
  /**
   * The stage the files are chosen at. The server cannot see it, so the
   * caller says; it decides whether a document is later shown as having
   * come from intake and settles nothing else.
   */
  source: FileSource;
  /** Told each file the server took, with what it stored it as. */
  onUploaded?: (uploaded: FileUploadResponse, file: File) => void;
  onError: (message: string | null) => void;
  onSettled: () => Promise<void>;
  /** +1 when an upload starts, -1 when it ends, so the page can wait on it. */
  onInFlight: (delta: number) => void;
  /** How many files are staged here, so the page can say they are unsent. */
  onStaged: (delta: number) => void;
}): SlotUploads {
  const pending = usePendingRows(onStaged);
  const { upload, started, busy, clearRefusals } = useSlotMutation({
    taskId,
    source,
    onUploaded,
    onRefused: keepRefused ? pending.keep : undefined,
    onError,
    onSettled,
    onInFlight,
  });

  const select = (files: File[]) => {
    const { fileType } = spec;
    onError(null);
    clearRefusals();
    // A slot that asks what a file is cannot accept one yet.
    if (spec.tags.length > 0) {
      pending.stage(files);
      return;
    }
    for (const file of files) {
      started();
      upload.mutate({ file, fileType, reportTo: "slot" });
    }
  };

  const send = sendRow({ pending, upload, started });

  const add = (id: string, tagId: SlotTag["id"]) => {
    const row = pending.rows.find((r) => r.id === id);
    const tag = spec.tags.find((t) => t.id === tagId);
    if (row === undefined || tag === undefined) return;
    onError(null);
    send(row, tag.fileType, tag);
  };

  const retry = (id: string) => {
    const { fileType } = spec;
    const row = pending.rows.find((r) => r.id === id);
    if (row === undefined) return;
    send(row, fileType);
  };

  return {
    busy,
    pending: pending.rows,
    select,
    choose: pending.choose,
    write: pending.write,
    add,
    discard: pending.discard,
    discardAll: pending.discardAll,
    retry,
  };
}
